import { ReactNode, useEffect, useState } from 'react';
import Box from "@mui/material/Box";
import Footer from "./Footer";
import Header from './Header';
import { Sidebar } from './Sidebar';
import SignalrService from '../../services/signalrService';
import bellSoundPath from '../../assets/sounds/bell.mp3';
import { Alert, Link, Snackbar } from '@mui/material';
import InfoAlert from './InfoAlert';
import { ROUTES } from '../../constants/routes';
import { SignalrEvents } from "../../models/enums/signalrEvents";
import { MedicalCheckupListChangeType } from "../../models/enums/medicalCheckupListChangeType";
import { MedicalCheckupStatus } from "../../models/enums/medicalCheckupStatus";
import { MedicalCheckupListChangedEvent } from "../../models/medicalCheckups/medicalCheckupListChangedEvent";
import { isCurrentTenantCheckup } from '../../helpers/medicalCheckupTenantGuard';
import { notificationsService } from '../../services/notificationsService';
import { DesktopNotificationDto } from '../../models/notifications/desktopNotificationDto';
import { permissionService } from '../../services/permissionService';
import { RoleType } from '../../models/enums/roleType';
import { DesktopNotificationsModal } from './DesktopNotificationsModal';

interface AppLayoutProps {
  title: string;
  children: ReactNode;
  hasReturnButton?: boolean;
}

export const Layout: React.FC<AppLayoutProps> = ({ title, children, hasReturnButton }) => {
  const [message, setMessage] = useState<ReactNode>(null);
  const [originalDocumentTitle] = useState<string>(document.title);
  const [count, setCount] = useState<number>(0);
  const [blinkIntervalId, setBlinkIntervalId] = useState<NodeJS.Timeout | null>(null);
  const { subscribeEvent, unsubscribeEvent } = SignalrService();
  const [desktopNotifications, setDesktopNotifications] = useState<DesktopNotificationDto[]>([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [toastQueue, setToastQueue] = useState<DesktopNotificationDto[]>([]);
  const [currentToast, setCurrentToast] = useState<DesktopNotificationDto | null>(null);

  useEffect(() => {
    subscribeEvent(SignalrEvents.MedicalCheckupListChanged, onMedicalCheckupListChanged);
    if (permissionService.hasRole(RoleType.Admin) || permissionService.hasRole(RoleType.Medic)) {
      loadDesktopNotifications();
    }

    const onTabVisibilityChange = () => {
      if (!document.hidden) { // user open tab
        stopBlinking();
      } else { // user leave tab
        setCount(0);
      }
    };

    document.addEventListener("visibilitychange", onTabVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onTabVisibilityChange);
      stopBlinking();
      unsubscribeEvent(SignalrEvents.MedicalCheckupListChanged, onMedicalCheckupListChanged);
    };
  }, []);

  useEffect(() => {
    if (count > 0) {
      if (document.hidden) {
        startBlinking();
      }
    } else {
      stopBlinking();
    }
  }, [count]);

  useEffect(() => {
    if (!currentToast && toastQueue.length > 0) {
      setCurrentToast(toastQueue[0]);
    }
  }, [toastQueue, currentToast]);

  const startBlinking = () => {
    if (blinkIntervalId) clearInterval(blinkIntervalId);

    const message = `(${count}) Новые медосмотры`;
    let isShown = true;
    let newBlinkIntervalId = setInterval(() => {
      document.title = isShown ? message : originalDocumentTitle;
      isShown = !isShown;
    }, 1000);
    setBlinkIntervalId(newBlinkIntervalId);
  };

  const stopBlinking = () => {
    setCount(0);

    if (blinkIntervalId) {
      clearInterval(blinkIntervalId);
      setBlinkIntervalId(null);
    }

    document.title = originalDocumentTitle;
  };

  const onMedicalCheckupListChanged = (event: MedicalCheckupListChangedEvent) => {
    if (!isCurrentTenantCheckup(event.checkup)) {
      return;
    }

    if (event.changeType !== MedicalCheckupListChangeType.Created || event.checkup.status !== MedicalCheckupStatus.New) {
      return;
    }

    if (!permissionService.hasRole(RoleType.Medic)) {
      return;
    }

    const url = ROUTES.medicalCheckups.idPath.replace(':id', String(event.checkup.id));
    const messageElement = (
      <span>
        <Link href={url} target="_blank" rel="noopener noreferrer">Получен новый медосмотр</Link>
      </span>
    );
    setMessage(messageElement);
    setCount(prevCount => prevCount + 1);
    playSound();
  };

  const loadDesktopNotifications = async () => {
    try {
      const unread = await notificationsService.getUnread();
      setDesktopNotifications(unread);
      setToastQueue(unread);
    } catch (e) {
      console.error(e);
    }
  };

  const handleBellClick = () => {
    setBellOpen(true);
  };

  const handleNotificationRead = async () => {
    if (desktopNotifications.length === 0) {
      setBellOpen(false);
      return;
    }

    const [current, ...rest] = desktopNotifications;
    
    // Закрываем диалог с анимацией
    setBellOpen(false);
    
    try {
      await notificationsService.markAsRead(current.id);
      setDesktopNotifications(rest);
      setToastQueue(prev => prev.filter(x => x.id !== current.id));
      
      // Открываем диалог снова с новым уведомлением после завершения анимации закрытия
      setTimeout(() => {
        if (rest.length > 0) {
          setBellOpen(true);
        }
      }, 250);
    } catch (e) {
      console.error(e);
      // В случае ошибки открываем диалог обратно
      setBellOpen(true);
    }

  };

  const handleToastClose = () => {
    setToastQueue(prev => prev.slice(1));
    setCurrentToast(null);
  };

  const playSound = () => {
    const volume = window.__env.settings.volume/100;
    const audio = new Audio(bellSoundPath);
    audio.volume = volume;
    audio.play().catch(error => console.error('Error playing sound:', error));
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh" sx={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      
      <Header
        title={title}
        hasReturnButton={hasReturnButton}
        notificationCount={desktopNotifications.length}
        onBellClick={handleBellClick}
      />
      {message && (
        <InfoAlert
          message={message}
          onClose={() => setMessage(null)}
          style={{ marginLeft: 250 }}
        />
      )}
      <Box display="flex" flexGrow={1} sx={{ overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
        <Sidebar />
        <Box component="main" sx={{
          flexGrow: 1,
          mt: 0.1,
          ml: { sm: 30 },
          p: 1.5,
          marginTop: -2,
          width: 0, // This forces flexbox to respect max-width constraints
          minWidth: 0, // Allows flex item to shrink below content size
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          {children}
        </Box>
      </Box>
      <Footer />

      <DesktopNotificationsModal
        open={bellOpen}
        notifications={desktopNotifications}
        onClose={() => setBellOpen(false)}
        onNotificationRead={handleNotificationRead}
      />

      {currentToast && (
        <Snackbar
          open={true}
          onClose={handleToastClose}
          autoHideDuration={10000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity="info"
            onClose={handleToastClose}
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {currentToast.text}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};